import { useState, useMemo, useEffect, useCallback } from "react";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

// Images
import registerImage from "assets/images/register.png";
import fitifyLogo from "assets/images/nFitifyLogo.svg";

import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  validateEmail,
  validatePhone,
  validatePassword,
  loadScript,
} from "utils/common";
import {
  userSelector,
  clearState,
  registerUser,
} from "store/features/authSlice";
import { useNavigate } from "react-router-dom";
import Logomain from "assets/images/creware.png";
import {
  collection,
  getDocs,
  query,
  where,
  arrayUnion,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { firestore } from "firebaseConfig";

import { Icon } from "@mui/material";
import Createable from "containers/Select";
import { fetchSubscriptionPlans } from "services/auth";
import { getIntlPhoneNumber } from "store/features/authSlice";
import { createRazorpayOrder } from "firebaseConfig";
import { setCreatedUserId } from "store/features/authSlice";

// Temporarily hide the 1-month plan from the sign-up subscription dropdown.
const HIDE_ONE_MONTH_PLAN = false;

function SignUp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [agreement, setAgremment] = useState(true);
  const [intlPhoneNumberArray, setIntlPhoneNumberArray] = useState([]);
  const {
    intlPhoneNumber,
    isSuccess,
    isError,
    isFetching,
    message,
    createdUserId,
  } = useSelector(userSelector);

  useEffect(() => {
    if (intlPhoneNumber) {
      const updateIntlNumber = intlPhoneNumber.data.map((item, index) => {
        return {
          label: `${
            item.dial_code.length === 2
              ? `(${item.dial_code})`.padEnd(10)
              : item.dial_code.length === 3
              ? `(${item.dial_code})`.padEnd(9)
              : item.dial_code.length === 4
              ? `(${item.dial_code})`.padEnd(8)
              : `(${item.dial_code})`
          } ${
            item?.name?.length > 15
              ? `${item?.name?.slice(0, 15)}...`
              : item?.name
          }`,
          value: item.dial_code,
          key: item.code,
          phoneLength: item.phoneLength,
        };
      });

      setIntlPhoneNumberArray(updateIntlNumber);
    }
  }, [intlPhoneNumber]);

  useEffect(() => {
    if (isError) {
      toast.error(message);
      dispatch(clearState());
    }

    if (isSuccess) {
      toast.success(message);

      if (message?.toLowerCase()?.includes("activated")) {
        navigate("/login");
      }

      dispatch(clearState());
    }
  }, [isError, message, dispatch, isSuccess, navigate]);

  useEffect(() => {
    dispatch(getIntlPhoneNumber());
  }, []);

  async function checkEmailExists(email) {
    const profilesRef = collection(firestore, "profile");
    const q = query(profilesRef, where("email", "==", email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  }

  //default fields value
  const defaultHandler = useMemo(() => {
    return {
      name: null,
      phoneNumber: null,
      email: null,
      password: null,
      referenceCode: null,
      gstNumber: "",
      gstName: "",
      countryCode: {
        label: "(+91)     India",
        value: "+91",
        key: "IN",
        phoneLength: 10,
      },
    };
  }, []);

  //default error fields value
  const errorDefault = useMemo(() => {
    return {
      name: false,
      phoneNumber: false,
      email: false,
      password: false,
      referenceCode: false,
    };
  }, []);

  const [formdata, setFormdata] = useState(defaultHandler);
  const [errorMessage, setErrorMessage] = useState(errorDefault);

  //for handling agreement
  const handleSetAgremment = () => setAgremment(!agreement);
  //for registering
  const registerHandler = async (
    payResponse,
    paymentData,
    referenceDetails
  ) => {
    if (referenceDetails !== null) {
      let ref = doc(firestore, "profile", referenceDetails.ref);
      await updateDoc(ref, {
        referrals: arrayUnion({
          name: formdata.name,
          email: formdata.email,
          date: new Date(),
          amount: referenceDetails.amount,
          redeemed: false,
        }),
      });
    }

    let referredBy = referenceDetails?.name ?? null;

    // Remove gstNumber and firmName if hasGst is not checked or values are empty/undefined
    let cleanFormdata = { ...formdata };
    if (!formdata.hasGst) {
      delete cleanFormdata.gstNumber;
      delete cleanFormdata.firmName;
    } else {
      if (!cleanFormdata.gstNumber) delete cleanFormdata.gstNumber;
      if (!cleanFormdata.firmName) delete cleanFormdata.firmName;
    }
    //  clean out undefined values
    cleanFormdata = Object.fromEntries(
      Object.entries(cleanFormdata).filter(([_, v]) => v !== undefined)
    );

    //  send only safe data
    dispatch(
      registerUser({
        formdata: { ...cleanFormdata, userId: createdUserId },
        payResponse,
        referredBy,
        paymentData,
        isActivation: true,
      })
    );
  };

  const [subscriptionType, setSubscriptionType] = useState([]);
  const [subscription, setSubscription] = useState();

  const [referralApplied, setReferralApplied] = useState(false);
  const [referenceDetails, setReferenceDetails] = useState(null);

  const onSelectSubscrition = (plan) => {
    const planWithOriginal = {
      ...plan,
      originalReferralAmount: plan.referralAmount ?? 0,
    };

    // check if referral code is currently applied
    if (referralApplied) {
      setSubscription({
        ...planWithOriginal,
        referralAmount: planWithOriginal.originalReferralAmount,
      });
    } else {
      setSubscription({
        ...planWithOriginal,
        referralAmount: 0,
      });
    }
  };

  const validateReferralCode = async (code, currentPlan = subscription) => {
    if (!code || code.trim() === "") {
      setReferralApplied(false);
      setReferenceDetails(null);
      if (currentPlan) {
        setSubscription({
          ...currentPlan,
          referralAmount: 0,
        });
      }

      return false;
    }

    const q = query(
      collection(firestore, "profile"),
      where("code", "==", code.trim())
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      const referredByName = docSnap.data().name;
      const referredByEmail = docSnap.data().email;

      setReferralApplied(true);
      setReferenceDetails({
        ref: docSnap.id,
        name: referredByName,
        email: referredByEmail,
      });
      if (currentPlan) {
        setSubscription({
          ...currentPlan,
          referralAmount: currentPlan.originalReferralAmount ?? 0,
        });
      }

      return true;
    } else {
      setReferralApplied(false);
      setReferenceDetails(null);

      if (currentPlan) {
        setSubscription({
          ...currentPlan,
          referralAmount: 0,
        });
      }

      return false;
    }
  };
  useEffect(() => {
    const validate = async () => {
      if (subscription) {
        await validateReferralCode(formdata.referenceCode);
      }
    };

    validate();
  }, [formdata.referenceCode]);

  const computedPlanDetails = useMemo(() => {
    if (!subscription) return null;

    const baseAmount = parseFloat(subscription.paymentAmount || 0);
    const gstPercentage = subscription.gstPercentage || 18;
    const referralPercent = referralApplied
      ? subscription.referralPercent || 5
      : 0;

    const referralDiscount = (baseAmount * referralPercent) / 100;
    const discountedBaseAmount = baseAmount - referralDiscount;

    const gstAmount = (discountedBaseAmount * gstPercentage) / 100;
    const finalAmount = discountedBaseAmount + gstAmount;

    return {
      baseAmount,
      gstPercentage,
      referralPercent,
      referralDiscount,
      gstAmount,
      totalAmount: baseAmount + (baseAmount * gstPercentage) / 100,
      finalAmount,
    };
  }, [subscription, referralApplied]);

  const [loader, setLoader] = useState(false);

  const displayRazorpay = async () => {
    const gstRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    const isGstValid = gstRegex.test(formdata.gstNumber ?? "");
    let paymentData = subscription;
    let referral = false;
    let referenceDetailsLocal = null;
    let isPendingUser = false;

    // Basic email validation upfront
    if (!validateEmail(formdata.email)) {
      toast.info("Enter a valid Email");
      setErrorMessage((prev) => ({ ...prev, email: true }));
      return;
    }

    try {
      setLoader(true);

      // ---------------- Check if user exists ----------------
      const profilesRef = collection(firestore, "profile");
      const q = query(
        profilesRef,
        where("email", "==", formdata.email?.toLowerCase())
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const existingDoc = querySnapshot.docs[0];
        const profileData = existingDoc.data();

        // ---------------- Prefill country code ----------------
        const prefilledCountryCode = intlPhoneNumberArray.find(
          (item) => item.value === profileData.countryCode
        ) || {
          label: "(+91) India",
          value: "+91",
          key: "IN",
          phoneLength: 10,
        };

        // ---------------- Prefill form ----------------
        setFormdata({
          ...formdata,
          name: profileData.name || "",
          phoneNumber: profileData.phoneNumber || "",
          referenceCode: profileData.referenceCode || "",
          gstNumber: profileData.gstNumber || "",
          firmName: profileData.firmName || "",
          countryCode: prefilledCountryCode,
        });

        // ---------------- Prefill subscription ----------------
        if (profileData.activeSubscription) {
          const sub = profileData.activeSubscription;
          setSubscription({
            plan: sub.plan,
            months: sub.monthOfSubscription,
            paymentAmount: profileData.payment[0]?.baseAmount || 0,
            referralAmount: profileData.payment[0]?.referralAmount || 0,
            gstPercentage: profileData.payment[0]?.gstPercentage || 18,
            value: sub.monthOfSubscription,
            label: `${sub.plan} (${sub.monthOfSubscription} month${
              sub.monthOfSubscription > 1 ? "s" : ""
            }, ₹${profileData.payment[0]?.baseAmount})`,
            originalReferralAmount: subscription?.originalReferralAmount || 0,
          });
        }

        // ---------------- Pending payment check ----------------
        const pendingPayment = Array.isArray(profileData.payment)
          ? profileData.payment.some((p) => p.paymentStatus === "pending")
          : false;

        if (pendingPayment) {
          dispatch(setCreatedUserId(existingDoc.id));
          toast.info(
            "You have a pending signup. Please complete your payment."
          );
          isPendingUser = true;
        } else {
          toast.error("Client with this Email already exists and is active.");
          setLoader(false);
          return;
        }
      }

      // ---------------- Referral code validation ----------------
      if (formdata.referenceCode?.trim()) {
        const qRef = query(
          collection(firestore, "profile"),
          where("code", "==", formdata.referenceCode.trim())
        );
        const refSnapshot = await getDocs(qRef);

        if (!refSnapshot.empty) {
          const docSnap = refSnapshot.docs[0];
          referral = true;
          referenceDetailsLocal = {
            ref: docSnap.id,
            name: docSnap.data().name,
            amount: computedPlanDetails.referralDiscount,
          };
        } else {
          referenceDetailsLocal = null;
          toast.info("Invalid referral code provided");
        }
      }

      // ---------------- Validations for NEW users only ----------------
      if (!isPendingUser) {
        if (!formdata.name) {
          toast.info("Enter name");
          setErrorMessage((prev) => ({ ...prev, name: true }));
          setLoader(false);
          return;
        }

        if (
          !formdata.phoneNumber ||
          formdata.phoneNumber.length <
            (formdata.countryCode.key === "IN"
              ? 10
              : formdata.countryCode.phoneLength)
        ) {
          toast.info("Invalid phone number");
          setLoader(false);
          return;
        }

        if (!formdata.password) {
          setErrorMessage((prev) => ({ ...prev, password: "Password is required." }));
          setLoader(false);
          return;
        }
        const passwordValidation = validatePassword(formdata.password);
        if (!passwordValidation.isValid) {
          setErrorMessage((prev) => ({ ...prev, password: passwordValidation.message }));
          setLoader(false);
          return;
        }

        if (!subscription) {
          toast.info("Choose a subscription plan");
          setLoader(false);
          return;
        }

        if (!agreement) {
          toast.info("Agree to the terms to continue");
          setLoader(false);
          return;
        }

        if (formdata.hasGst) {
          if (!formdata.gstNumber?.trim()) {
            setErrorMessage((prev) => ({ ...prev, gstNumber: true }));
            toast.info("Enter GST number!");
            setLoader(false);
            return;
          }
          if (!isGstValid) {
            setErrorMessage((prev) => ({ ...prev, gstNumber: true }));
            toast.error("Invalid GST number!");
            setLoader(false);
            return;
          }
          if (!formdata.firmName?.trim()) {
            setErrorMessage((prev) => ({ ...prev, firmName: true }));
            toast.error("Enter valid firm name!");
            setLoader(false);
            return;
          }
        }
      }

      // ---------------- Create pending user ----------------
      const createResult = await dispatch(
        registerUser({
          formdata,
          payResponse: null,
          referredBy: null,
          paymentData: subscription,
          isActivation: false,
        })
      ).unwrap();

      const pendingUserId = createResult.userId;
      dispatch(setCreatedUserId(pendingUserId));

      // ---------------- Load Razorpay SDK ----------------
      const res = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js"
      );
      if (!res) {
        toast.error("Razorpay SDK failed to load");
        setLoader(false);
        return;
      }

      // ---------------- Create Razorpay order ----------------
      const roundedFinalAmount = Math.round(
        computedPlanDetails.finalAmount * 100
      );
      const { data } = await createRazorpayOrder({
        amount: roundedFinalAmount,
      });

      if (pendingUserId) {
        const profileRef = doc(firestore, "profile", pendingUserId);
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists()) {
          const existingData = profileSnap.data();

          // Ensure payment is an array
          const existingPayments = Array.isArray(existingData.payment)
            ? existingData.payment
            : [];

          // Update the payment you want (e.g., first payment)
          const updatedPayments = existingPayments.map((p, idx) =>
            idx === 0
              ? { ...p, razorpay_order_id: data.order.id } // update fields
              : p
          );

          // Write the entire array back to Firestore
          await updateDoc(profileRef, {
            payment: updatedPayments,
          });
        }
      }
      // ---------------- Razorpay options ----------------
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        name: "Fitify",
        description: "Sign up transaction",
        image: Logomain,
        currency: "INR",
        order_id: data.order.id,
        prefill: {
          name: formdata.name,
          email: formdata.email,
          contact: formdata.phoneNumber,
        },
        handler: function (response) {
          response = {
            ...response,
            subscriptionAmount: computedPlanDetails.baseAmount,
            gstAmount: computedPlanDetails.gstAmount,
            totalAmount: computedPlanDetails.totalAmount,
            amount: roundedFinalAmount / 100,
            referralAmount: computedPlanDetails.referralDiscount,
          };
          registerHandler(response, paymentData, referenceDetailsLocal);
        },
      };

      const paymentObj = new window.Razorpay(options);
      paymentObj.open();
      setLoader(false);
    } catch (err) {
      setLoader(false);
      toast.error("Something went wrong, retry!");
      console.error(err);
    }
  };

  const [showPass, setShowPass] = useState(false);
  const fetchSubscriptions = useCallback(async () => {
    try {
      let subs = [];
      setLoader(true);
      subs = await fetchSubscriptionPlans();
      const formattedSubs = [...subs]
        .filter((plan) => !(HIDE_ONE_MONTH_PLAN && plan.months === 1))
        .sort((a, b) => a.months - b.months)
        .map((plan) => {
          return {
            ...plan,
            label: `${plan.plan} (${plan.months} month${
              plan.months > 1 ? "s" : ""
            }, ₹${plan.paymentAmount})`,
          };
        });
      setSubscriptionType(formattedSubs);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      toast.error("Something went wrong, retry");
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#1A1A1A",
        display: "flex",
        alignItems: "center",
        fontFamily: "Outfit",
        position: "relative",
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="stretch">
          {/* Left Side - Image */}
          {/* Logo - Absolute positioned aligned with content */}
          <Box
            sx={{
              position: "absolute",
              top: 20,
              left: 8,
              zIndex: 1000,
            }}
          >
            <Box
              component="img"
              src={fitifyLogo}
              alt="Fitify Logo"
              onClick={() => navigate("/")}
              sx={{
                height: "50px",
                width: "auto",
                maxWidth: "150px",
                cursor: "pointer",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            />
          </Box>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            {/* Title */}
            <Typography
              variant="h1"
              sx={{
                color: "#17C1E8",
                fontWeight: "bold",
                mb: 2,
                fontFamily: "Outfit",
                fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
                textAlign: "center",
                width: "100%",
              }}
            >
              Welcome!
            </Typography>

            <Box
              sx={{
                height: { xs: "300px", sm: "400px", md: "600px" },
                borderRadius: "20px",
                overflow: "hidden",
                position: "relative",
                width: "100%",
              }}
            >
              <Box
                component="img"
                src={registerImage}
                alt="Register"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: { xs: "cover", sm: "cover", md: "contain" },
                  objectPosition: { xs: "center", sm: "center", md: "center" },
                  borderRadius: "20px",
                }}
              />
            </Box>
          </Grid>

          {/* Right Side - Register Form */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                borderRadius: "20px",
                padding: { xs: 2, sm: 3, md: 4 },
                textAlign: "center",
                minHeight: { xs: "auto", md: "600px" },
                display: "flex",
                flexDirection: "column",
                justifyContent: { xs: "flex-start", md: "center" },
                overflowY: { xs: "auto", md: "visible" },
                maxHeight: { xs: "80vh", md: "none" },
              }}
            >
              {/* Subtitle */}
              <Typography
                variant="h5"
                sx={{
                  color: "#FFFFFF",
                  fontWeight: "medium",
                  mb: { xs: 1, sm: 2 },
                  fontFamily: "Outfit",
                  fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.75rem" },
                }}
              >
                Register to Fitify
              </Typography>

              {/* Description */}
              {/* <Typography
                variant="body1"
                sx={{
                  color: "#FFFFFF",
                  mb: 4,
                  fontFamily: "Outfit",
                  opacity: 0.8,
                }}
              >
                Register or login to Fitify and enter to the world of better tomorrow for your business
              </Typography> */}

              {/* Register Form */}
              <Box component="form" sx={{ textAlign: "left", width: "100%" }}>
                {/* Name Field */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      mb: 1,
                      fontWeight: "bold",
                      fontFamily: "Outfit",
                    }}
                  >
                    Name*
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your name"
                    value={formdata?.name ?? ""}
                    error={errorMessage?.name}
                    onChange={(e) => {
                      let numberRegex = /^[a-zA-Z ]*$/;
                      let val = e.target.value;
                      let match_ = val.match(numberRegex);
                      if (!match_ && val) {
                        return false;
                      }
                      setErrorMessage({
                        ...errorMessage,
                        name: false,
                      });
                      setFormdata({
                        ...formdata,
                        name: val,
                      });
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFFFFF",
                        borderRadius: "10px",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "2px solid #17C1E8",
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontFamily: "Outfit",
                        padding: "12px 16px",
                      },
                    }}
                  />
                </Box>

                {/* Email Field */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      mb: 1,
                      fontWeight: "bold",
                      fontFamily: "Outfit",
                    }}
                  >
                    Email*
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="Enter your email"
                    value={formdata?.email ?? ""}
                    error={errorMessage?.email}
                    onChange={(e) => {
                      setErrorMessage({
                        ...errorMessage,
                        email: false,
                      });
                      setFormdata({
                        ...formdata,
                        email: e.target.value,
                      });
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFFFFF",
                        borderRadius: "10px",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "2px solid #17C1E8",
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontFamily: "Outfit",
                        padding: "12px 16px",
                      },
                    }}
                  />
                </Box>

                {/* Phone Number Field */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      mb: 1,
                      fontWeight: "bold",
                      fontFamily: "Outfit",
                    }}
                  >
                    Phone Number*
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box sx={{ width: { xs: "100%", sm: "30%" } }}>
                      <Createable
                        defaultMenuIsOpen
                        isOpen
                        options={intlPhoneNumberArray}
                        style={{ borderRadius: "0.5rem", fontSize: 14 }}
                        inputStyle={{
                          width: { xs: "100%", sm: 190 },
                          height: "1.900rem",
                          borderRadius: "0.5rem",
                        }}
                        onChange={(e) => {
                          setFormdata({
                            ...formdata,
                            countryCode: e,
                            phoneNumber: null,
                          });
                        }}
                        value={formdata.countryCode}
                      />
                    </Box>
                    <Box sx={{ width: { xs: "100%", sm: "70%" } }}>
                      <TextField
                        fullWidth
                        type="text"
                        placeholder="Phone Number"
                        value={formdata?.phoneNumber ?? ""}
                        error={errorMessage?.phoneNumber}
                        onChange={(e) => {
                          let numberRegex = /^\d+$/;
                          let val = e.target.value;
                          let match_ = val.match(numberRegex);
                          if (!match_ && val) {
                            return false;
                          }

                          if (
                            formdata.countryCode.key === "IN"
                              ? val.length > 10
                              : val.length > formdata?.countryCode?.phoneLength
                          ) {
                            toast.info("Invalid phone number");
                            return;
                          }

                          setErrorMessage({
                            ...errorMessage,
                            phoneNumber: false,
                          });
                          setFormdata({
                            ...formdata,
                            phoneNumber: e.target.value,
                          });
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#FFFFFF",
                            borderRadius: "10px",
                            "& fieldset": {
                              border: "none",
                            },
                            "&:hover fieldset": {
                              border: "none",
                            },
                            "&.Mui-focused fieldset": {
                              border: "2px solid #17C1E8",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontFamily: "Outfit",
                            padding: "12px 16px",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Password Field */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      mb: 1,
                      fontWeight: "bold",
                      fontFamily: "Outfit",
                    }}
                  >
                    Password*
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      fullWidth
                      type={showPass ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formdata?.password ?? ""}
                      error={!!errorMessage?.password}
                      onChange={(e) => {
                        setErrorMessage({
                          ...errorMessage,
                          password: null,
                        });
                        setFormdata({
                          ...formdata,
                          password: e.target.value,
                        });
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#FFFFFF",
                          borderRadius: "10px",
                          paddingRight: "50px",
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover fieldset": {
                            border: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "2px solid #17C1E8",
                          },
                        },
                        "& .MuiInputBase-input": {
                          fontFamily: "Outfit",
                          padding: "12px 16px",
                        },
                      }}
                    />
                    <IconButton
                      onClick={() => setShowPass(!showPass)}
                      sx={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#666",
                      }}
                    >
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Box>
                  {errorMessage?.password && (
                    <Typography
                      variant="caption"
                      sx={{ color: "#FF6B6B", mt: 0.5, display: "block", fontFamily: "Outfit" }}
                    >
                      {errorMessage.password}
                    </Typography>
                  )}
                </Box>
                {/* Subscription Plan */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      mb: 1,
                      fontWeight: "bold",
                      fontFamily: "Outfit",
                    }}
                  >
                    Choose Subscription Plan*
                  </Typography>
                  <Createable
                    options={subscriptionType ?? []}
                    value={subscription}
                    style={{
                      borderRadius: "0.5rem",
                      fontSize: 14,
                      zIndex: 999,
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "1.800rem",
                      borderRadius: "0.5rem",
                    }}
                    placeholder="Subscription plan"
                    onChange={(e) => {
                      onSelectSubscrition(e);
                    }}
                  />
                </Box>

                {/* Reference Code Field */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      mb: 1,
                      fontWeight: "bold",
                      fontFamily: "Outfit",
                    }}
                  >
                    Reference Code (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter reference code"
                    value={formdata?.referenceCode ?? ""}
                    error={errorMessage?.referenceCode}
                    onChange={(e) => {
                      setErrorMessage({
                        ...errorMessage,
                        referenceCode: false,
                      });
                      setFormdata({
                        ...formdata,
                        referenceCode: e.target.value,
                      });
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFFFFF",
                        borderRadius: "10px",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "2px solid #17C1E8",
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontFamily: "Outfit",
                        padding: "12px 16px",
                      },
                    }}
                  />
                </Box>

                {/* GST Checkbox */}
                <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={formdata.hasGst}
                    onChange={() => {
                      if (formdata.hasGst) {
                        const updatedFormData = {
                          ...formdata,
                          hasGst: false,
                        };

                        delete updatedFormData.gstNumber;
                        delete updatedFormData.firmName;

                        setFormdata(updatedFormData);
                      } else {
                        setFormdata({
                          ...formdata,
                          hasGst: true,
                          gstNumber: "",
                          firmName: "",
                        });
                      }
                    }}
                    sx={{
                      color: "#17C1E8",
                      "&.Mui-checked": {
                        color: "#17C1E8",
                      },
                      padding: "4px",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Outfit",
                      whiteSpace: "nowrap",
                      marginLeft: "8px",
                    }}
                  >
                    I have a GST Number
                  </Typography>
                </Box>

                {/* GST Fields */}
                {formdata.hasGst && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#FFFFFF",
                          mb: 1,
                          fontWeight: "bold",
                          fontFamily: "Outfit",
                        }}
                      >
                        GST Number*
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter GST number"
                        value={formdata?.gstNumber ?? ""}
                        error={errorMessage?.gstNumber}
                        onChange={(e) => {
                          setErrorMessage({
                            ...errorMessage,
                            gstNumber: false,
                          });
                          setFormdata({
                            ...formdata,
                            gstNumber: e.target.value.toUpperCase(),
                          });
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#FFFFFF",
                            borderRadius: "10px",
                            "& fieldset": {
                              border: "none",
                            },
                            "&:hover fieldset": {
                              border: "none",
                            },
                            "&.Mui-focused fieldset": {
                              border: "2px solid #17C1E8",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontFamily: "Outfit",
                            padding: "12px 16px",
                          },
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#FFFFFF",
                          mb: 1,
                          fontWeight: "bold",
                          fontFamily: "Outfit",
                        }}
                      >
                        Firm Name*
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter firm name"
                        value={formdata?.firmName ?? ""}
                        error={errorMessage?.firmName}
                        onChange={(e) => {
                          setErrorMessage({
                            ...errorMessage,
                            firmName: false,
                          });
                          setFormdata({
                            ...formdata,
                            firmName: e.target.value,
                          });
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#FFFFFF",
                            borderRadius: "10px",
                            "& fieldset": {
                              border: "none",
                            },
                            "&:hover fieldset": {
                              border: "none",
                            },
                            "&.Mui-focused fieldset": {
                              border: "2px solid #17C1E8",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontFamily: "Outfit",
                            padding: "12px 16px",
                          },
                        }}
                      />
                    </Box>
                  </>
                )}

                {/* Plan Summary */}
                {computedPlanDetails && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      backgroundColor: "rgba(23, 193, 232, 0.1)",
                      borderRadius: "10px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ color: "#17C1E8", fontFamily: "Outfit", mb: 1 }}
                    >
                      💳 Plan Summary
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#FFFFFF", fontFamily: "Outfit", mb: 0.5 }}
                    >
                      Base Amount: ₹{computedPlanDetails.baseAmount.toFixed(2)}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: "#FFFFFF", fontFamily: "Outfit", mb: 0.5 }}
                    >
                      GST ({computedPlanDetails.gstPercentage}%): ₹
                      {computedPlanDetails.gstAmount.toFixed(2)}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: "#FFFFFF", fontFamily: "Outfit", mb: 0.5 }}
                    >
                      Total (Incl. GST): ₹
                      {computedPlanDetails.totalAmount.toFixed(2)}
                    </Typography>

                    {referralApplied &&
                      computedPlanDetails.referralDiscount > 0 && (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#4CAF50",
                              fontFamily: "Outfit",
                              mb: 0.5,
                            }}
                          >
                            Referral Discount (
                            {computedPlanDetails.referralPercent
                              ? computedPlanDetails.referralPercent
                              : 5}
                            %) : -₹
                            {computedPlanDetails.referralDiscount.toFixed(2)}
                          </Typography>

                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: "#17C1E8", fontFamily: "Outfit" }}
                          >
                            Final Amount: ₹
                            {computedPlanDetails.finalAmount.toFixed(2)}
                          </Typography>
                        </>
                      )}
                  </Box>
                )}

                {/* Terms and Conditions */}
                <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={agreement}
                    onChange={handleSetAgremment}
                    sx={{
                      color: "#17C1E8",
                      "&.Mui-checked": {
                        color: "#17C1E8",
                      },
                      padding: "4px",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Outfit",
                      marginLeft: "8px",
                    }}
                  >
                    I agree the{" "}
                    <Typography
                      component="a"
                      href="https://creware.asia/terms&conditions/"
                      target="_blank"
                      sx={{
                        color: "#17C1E8",
                        textDecoration: "underline",
                        fontFamily: "Outfit",
                      }}
                    >
                      Terms and Conditions
                    </Typography>
                  </Typography>
                </Box>

                {/* Submit Button */}
                <Button
                  fullWidth
                  variant="contained"
                  disabled={isFetching || loader}
                  onClick={displayRazorpay}
                  sx={{
                    backgroundColor: "#17C1E8",
                    color: "#000000",
                    fontWeight: "bold",
                    padding: "12px",
                    borderRadius: "10px",
                    fontSize: "16px",
                    fontFamily: "Outfit",
                    textTransform: "none",
                    mb: 3,
                    "&:hover": {
                      backgroundColor: "#14A8C4",
                    },
                    "&:disabled": {
                      backgroundColor: "#666",
                      color: "#999",
                    },
                  }}
                >
                  {isFetching || loader ? "Loading..." : "Sign Up"}
                </Button>

                {/* Sign In Link */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      fontFamily: "Outfit",
                    }}
                  >
                    Already have an account?{" "}
                    <Typography
                      component={Link}
                      to="/login"
                      sx={{
                        color: "#17C1E8",
                        cursor: "pointer",
                        fontWeight: "bold",
                        fontFamily: "Outfit",
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Sign in
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default SignUp;
